import { UIChunkType } from '@myre/app-agent-domain';
import { type UiLibI18n } from '@myre/domain-i18n-wordings';

import { Component, computed, inject, input, signal } from '@angular/core';
import { isDataUIPart } from 'ai';

import { WORDINGS } from '../../../myre-wordings-loader';

import {
  type Citation,
  type MyreUiMessagePart,
} from '../../interfaces/chat.interfaces';
import { CitationModalService } from '../../services/citation-modal.service';

/**
 * Renders grounded-synthesis citations as a collapsible sources block.
 * Each citation shows a numbered chip, the statement, and the source line
 * (worker + tools used). Clicking a source opens a modal with the raw tool
 * results backing the claim (CitationDetailComponent).
 */
@Component({
  selector: 'app-citation-sources',
  templateUrl: './citation-sources.component.html',
  styleUrl: './citation-sources.component.scss',
})
export class CitationSourcesComponent {
  public readonly part = input.required<MyreUiMessagePart>();

  private readonly _citationModalService = inject(CitationModalService);
  private readonly _wordings = inject<UiLibI18n>(WORDINGS);

  protected get chatWordings() {
    return this._wordings._library.chat;
  }

  protected readonly _citations = computed<Citation[]>(() => {
    const part = this.part();

    if (!isDataUIPart(part) || part.type !== UIChunkType.DataCitation) {
      return [];
    }

    return part.data.citations;
  });

  /** Citations are collapsed by default; the user opens them on demand. */
  protected readonly _expanded = signal(false);

  protected get _isExpanded(): boolean {
    return this._expanded();
  }

  protected _toggle(): void {
    this._expanded.set(!this._isExpanded);
  }

  /** Worker names of a citation's steps, deduplicated. */
  protected _workersOf(citation: Citation): string {
    const workers = new Set(citation.steps.map(s => s.worker_name));

    return [...workers].join(', ');
  }

  /** Tool names backing a citation, deduplicated across its steps. */
  protected _toolsOf(citation: Citation): string {
    const tools = new Set<string>();

    for (const step of citation.steps) {
      for (const item of step.evidence ?? []) {
        tools.add(item.tool);
      }
    }

    return [...tools].join(', ');
  }

  protected _openDetail(citation: Citation): void {
    this._citationModalService.open(citation);
  }
}
